import { create } from 'zustand';
import axios from 'axios';
import { getApiBaseUrl } from '../utils/config';
import type { User } from '../types/dashboard';
import { useUserStore } from './UserStore';

interface HierarchicalUsersStore {
  users: User[];
  isLoading: boolean;
  error: string | null;
  lastFetched: number | null;

  fetchUsers: (forceRefresh?: boolean) => Promise<void>;
  clearUsers: () => void;
}

export const useHierarchicalUsersStore = create<HierarchicalUsersStore>((set, get) => ({
  users: [],
  isLoading: false,
  error: null,
  lastFetched: null,

  fetchUsers: async (forceRefresh = false) => {
    const state = get();

    // ✅ get current user synchronously (don’t call hook)
    const currentUser = useUserStore.getState().user;

    const token = localStorage.getItem('token');
    if (!token) {
      throw new Error('No authentication token found');
    }

    // ✅ caching logic
    const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes
    const now = Date.now();
    const hasRecentData =
      state.lastFetched && now - state.lastFetched < CACHE_DURATION;

    if (!forceRefresh && state.users.length > 0 && hasRecentData) return;
    if (state.isLoading) return;

    set({ isLoading: true, error: null });

    try {
      const response = await axios.get(`${getApiBaseUrl()}/api/v1/users/hierarchical`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      const fetchedUsers = response.data.users || [];
      const currentUserStats = response.data.current_user_stats;

      // ✅ Add current user safely with call stats from the API
      let currentUserWithStats = currentUser;
      if (currentUser && !fetchedUsers.some((u: any) => u.uid === currentUser.uid)) {
        currentUserWithStats = {
          ...currentUser,
          //@ts-ignore
          total_calls: currentUserStats?.total_calls || 0,
          average_score: currentUserStats?.average_score || 0,
        };
      }

      const combinedUsers = [...fetchedUsers, currentUserWithStats].filter(Boolean);

      const users = combinedUsers.map((u: any) => ({
        id: u.uid,
        name: u.name,
        role: u.role,
        designation: u.designation,
        uid: u.uid,
        head: u.head,
        email: u.email || '',
        status: u.status || 'unsuspended',
        core_metrics: u.core_metrics || null,
        total_calls: u.total_calls || 0,
        average_score: u.average_score || 0,
      }));


      set({
        //@ts-ignore
        users,
        isLoading: false,
        error: null,
        lastFetched: now,
      });
    } catch (error) {
      console.error('Error fetching hierarchical users:', error);
      set({
        error: error instanceof Error ? error.message : 'Failed to fetch users',
        isLoading: false,
      });
    }
  },

  clearUsers: () => {
    set({
      users: [],
      isLoading: false,
      error: null,
      lastFetched: null,
    });
  },
}));
